import { useMemo } from 'react'
import i18next from 'i18next'

import { MODAL_MODE } from 'components/Modal/config'
import { logEvent } from 'lib/analytics'
import { EXTERNAL_PATH, ROUTE_PATH } from 'lib/paths'
import { NavMenu } from 'lib/_types'
import usePageModal from 'features/Modal/hooks/usePageModal'

type Props = {
  isLoggedIn?: boolean
}

export const useNavConfig = ({ isLoggedIn }: Props): NavMenu[] => {
  const { generatePageModalUrl, pathname } = usePageModal()

  const menuList = useMemo<NavMenu[]>(() => {
    if (isLoggedIn) {
      return [
        {
          label: i18next.t('common.timeline'),
          key: 'timeline',
          to: ROUTE_PATH.HOME,
          needsLogin: true,
          pathKeys: [
            { path: ROUTE_PATH.HOME },
            { path: ROUTE_PATH.CONVERSATION(''), mode: 'startsWith' },
            { path: ROUTE_PATH.POST(''), mode: 'startsWith' },
            { path: ROUTE_PATH.TIMELINE_PUBLIC, mode: 'startsWith' },
          ],
          logKey: 'Timeline',
        },
        {
          label: i18next.t('common.group'),
          key: 'circle',
          to: ROUTE_PATH.GROUPS,
          pathKeys: [{ path: ROUTE_PATH.GROUP(''), mode: 'startsWith' }],
          logKey: 'Group',
        },
        {
          label: i18next.t('common.chat'),
          key: 'chat',
          to: generatePageModalUrl(MODAL_MODE.CHAT_LIST),
          onClick: () => logEvent.chat_room_start(),
          logKey: 'Chat',
        },
        {
          label: i18next.t('common.activity'),
          key: 'activity',
          to: ROUTE_PATH.ACTIVITIES,
          pathKeys: [{ path: ROUTE_PATH.ACTIVITIES, mode: 'startsWith' }],
          logKey: 'Notification',
        },
        {
          label: i18next.t('common.bookmarks'),
          key: 'bookmark',
          to: ROUTE_PATH.BOOKMARKS,
          displayIn: 'mobile',
          logKey: 'Bookmarks',
        },
        {
          label: i18next.t('common.setting'),
          key: 'setting',
          to: ROUTE_PATH.SETTING,
          displayIn: 'mobile',
          logKey: 'Settings',
        },
      ]
    } else {
      return [
        {
          label: i18next.t('common.home'),
          key: 'home',
          to: ROUTE_PATH.HOME,
          pathKeys: [
            { path: ROUTE_PATH.HOME },
            { path: ROUTE_PATH.CONVERSATION(''), mode: 'startsWith' },
          ],
          logKey: 'Home',
        },
        {
          label: i18next.t('common.timeline'),
          key: 'timeline',
          to: ROUTE_PATH.TIMELINE_PUBLIC,
          pathKeys: [{ path: ROUTE_PATH.TIMELINE_PUBLIC, mode: 'startsWith' }],
          logKey: 'Timeline',
        },
        {
          label: i18next.t('common.group'),
          key: 'circle',
          to: ROUTE_PATH.GROUPS_ALL,
          pathKeys: [{ path: ROUTE_PATH.GROUP(''), mode: 'startsWith' }],
          logKey: 'Group',
        },
        {
          label: i18next.t('common.portal'),
          key: 'about',
          to: EXTERNAL_PATH.LANDING_PAGE(),
          displayIn: 'desktop',
          isExternal: true,
          logKey: 'About',
        },
      ]
    }
  }, [isLoggedIn, pathname])

  return menuList
}
